<template>
  <section class="automotive-excellence-section">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="section-description">{{ headerData.description }}</p>
      </div>
      
      <div class="excellence-grid">
        <div class="excellence-card" v-for="(item, index) in excellenceData" :key="index">
          <div class="card-icon">
            <i :class="item.icon"></i>
          </div>
          <div class="card-content">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <div class="card-features">
              <span v-for="(feature, featureIndex) in item.features" :key="featureIndex" class="feature-badge">
                {{ feature }}
              </span>
            </div>
          </div>
          <div class="card-overlay">
            <div class="overlay-content">
              <h4>{{ item.title }}</h4>
              <p>{{ item.detailedDescription }}</p>
              <button class="excellence-btn">{{ buttonText }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceItsCardProComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-car",
        tag: "AUTOMOTIVE EXCELLENCE",
        title: "Premium Auto Care Solutions",
        description: "Experience the pinnacle of automotive service with our advanced technology and expert craftsmanship"
      })
    },
    excellenceData: {
      type: Array,
      required: true,
      default: () => []
    },
    buttonText: {
      type: String,
      required: false,
      default: "Learn More"
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Automotive Excellence Section */
.automotive-excellence-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.excellence-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.excellence-card {
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  border: 1px solid #e9ecef;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.excellence-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 25px 50px rgba(220, 53, 69, 0.2);
  border-color: #dc3545;
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  transition: all 0.3s ease;
}

.excellence-card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
}

.card-icon i {
  font-size: 32px;
  color: white;
}

.card-content {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.card-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.card-content p {
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 20px;
  line-height: 1.6;
}

.card-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
}

.feature-badge {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.95), rgba(200, 35, 51, 0.95));
  backdrop-filter: blur(10px);
  color: white;
  padding: 40px 30px;
  border-radius: 20px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(20px);
}

.excellence-card:hover .card-overlay {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.excellence-card:hover .card-content {
  opacity: 0;
  transform: translateY(-20px);
}

.overlay-content {
  text-align: center;
  transform: translateY(10px);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.excellence-card:hover .overlay-content {
  transform: translateY(0);
}

.overlay-content h4 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
  color: white;
}

.overlay-content p {
  font-size: 16px;
  margin-bottom: 25px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
}

.excellence-btn {
  background: white;
  color: #dc3545;
  border: none;
  padding: 12px 25px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.excellence-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.excellence-btn:hover::before {
  left: 100%;
}

.excellence-btn:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .excellence-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .excellence-card {
    padding: 30px 20px;
  }
  
  .section-header h2 {
    font-size: 36px;
  }
  
  .section-description {
    font-size: 16px;
  }
}
</style>
